import { AuthStates, useAuthState, useLogout } from '@vis-auth/react';
import { DashboardProfile, DashboardUser, UserStatus } from '@vis-auth/vis-user-client';
import React, { FC, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { PageLoader } from '@ver-uds/react';
import useDashboardApi from '../../services/DashboardApi';

type DashboardContextType = {
  users: DashboardUser[];
  legacyUsers: DashboardUser[];
  hasUsersError: boolean;
  usersAreLoading: boolean;
  profile: DashboardProfile | null;
  hasProfileError: boolean;
  reloadProfile: () => void;
};

const DashboardContext = createContext<DashboardContextType | null>(null);

export const useDashboard = (): DashboardContextType => {
  const dashboardContext = useContext(DashboardContext);

  if (!dashboardContext) {
    throw new Error('useDashboard must be used inside DashboardProvider');
  }

  return dashboardContext;
};

export const DashboardProvider: FC = ({ children }) => {
  const [users, setUsers] = useState<DashboardUser[]>([]);
  const [legacyUsers, setLegacyUsers] = useState<DashboardUser[]>([]);
  const [hasUsersError, setHasUsersError] = useState<boolean>(false);
  const [usersAreLoading, setUsersAreLoading] = useState<boolean>(true);

  const [profile, setProfile] = useState<DashboardProfile | null>(null);
  const [hasProfileError, setHasProfileError] = useState<boolean>(false);

  const [needsProfileReload, setNeedsProfileReload] = useState<boolean>(true);

  const authState = useAuthState();
  const dashboardApi = useDashboardApi();
  const logout = useLogout();

  if (authState.state !== AuthStates.authenticated) {
    throw new Error('DashboardProvider must be used inside SecureRoute');
  }

  // Get profile
  const fetchProfile = useCallback(async (): Promise<void> => {
    try {
      const getProfileResponse = await dashboardApi.getSharedProfile({});

      // Set profile
      setProfile(getProfileResponse);
    } catch (e: any) {
      if (e.status === 401) {
        // Log out if unauthorized
        logout();
      } else {
        // Show error if any other error
        setHasProfileError(true);
      }
    }
  }, [dashboardApi, logout]);

  // Get users
  const fetchUsers = useCallback(async (): Promise<void> => {
    try {
      const getUsersResponse = await dashboardApi.getDashboardUsers({});

      // Map to only MFA users with a non-deleted status
      const filteredUsers = getUsersResponse?.users?.filter(
        (user) => user.mfaEnabled && user.userStatus !== UserStatus.DELETED,
      );

      // Map to only non-MFA users
      const filteredLegacyUsers = getUsersResponse?.users?.filter((user) => !user.mfaEnabled);

      // Set dashboard users
      setUsers(filteredUsers || []);
      setLegacyUsers(filteredLegacyUsers || []);
      setUsersAreLoading(false);
    } catch (e: any) {
      if (e.status === 401) {
        // Log out if unauthorized
        logout();
      } else {
        // Show error if any other error
        setHasUsersError(true);
        setUsersAreLoading(false);
      }
    }
  }, [dashboardApi, logout]);

  useEffect(() => {
    if (needsProfileReload) {
      fetchProfile();
      setNeedsProfileReload(false);
    }

    fetchUsers();
  }, [dashboardApi, fetchProfile, fetchUsers, logout, needsProfileReload]);

  const reloadProfile = (): void => {
    setNeedsProfileReload(true);
  };

  // Show routes
  if (profile || hasProfileError) {
    return (
      <DashboardContext.Provider
        value={{ users, legacyUsers, hasUsersError, usersAreLoading, profile, hasProfileError, reloadProfile }}
      >
        {children}
      </DashboardContext.Provider>
    );
  }

  // Show page loader if get profile call is still loading
  return <PageLoader label="Loading" />;
};
